import { create } from 'zustand'
import { buildLsUserKey } from '@/storage/version'
import { getIdbUserScope } from '@/workspace/idb-cache'

/**
 * Recent ticker history for the research views. Persists per-user into
 * localStorage under `edge:{userSub}:recent-research-tickers`.
 *
 * Timing caveat (intentional): the store hydrates ONCE at module load
 * time. If the module is imported before auth completes, the userSub
 * scope is `'anonymous'` — the store starts empty (no user-scope data
 * yet) and writes from that point onward use whatever scope is current
 * at the moment of write. After login completes, subsequent writes
 * land under the real userSub key; the next page reload picks up that
 * key's contents. This is an accepted simplification — we don't try
 * to auto-rehydrate on userSub change.
 */
const MAX_TICKERS = 5
const KEY_SEGMENT = 'recent-research-tickers'

export interface RecentTicker {
  symbol: string
  name: string
}

interface RecentTickersState {
  tickers: RecentTicker[]
  addTicker: (symbol: string, name: string) => void
}

function loadTickers(): RecentTicker[] {
  try {
    const key = buildLsUserKey(getIdbUserScope(), KEY_SEGMENT)
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as RecentTicker[]) : []
  } catch {
    return []
  }
}

export const useRecentTickers = create<RecentTickersState>((set) => ({
  tickers: loadTickers(),
  addTicker: (symbol, name) =>
    set((state) => {
      // Callers that only know the symbol (a deep link) pass '' for name;
      // keep the name an earlier visit recorded.
      const known = state.tickers.find((t) => t.symbol === symbol)
      const filtered = state.tickers.filter((t) => t.symbol !== symbol)
      const next = [{ symbol, name: name || known?.name || '' }, ...filtered].slice(0, MAX_TICKERS)
      try {
        const key = buildLsUserKey(getIdbUserScope(), KEY_SEGMENT)
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        /* localStorage unavailable / quota exceeded */
      }
      return { tickers: next }
    }),
}))

/** Symbol the user last viewed in Research, or null before their first visit. */
export function useLastResearchTicker(): string | null {
  return useRecentTickers((s) => s.tickers[0]?.symbol ?? null)
}
